/**
 * Shared geodata helpers. Pure functions, used by both the build scripts and
 * the browser, so they are unit-testable without a DOM or a network.
 */

/** Denver. Camera target and the centre of every bbox we cut. */
export const DENVER = { longitude: -104.9903, latitude: 39.7392 };

/**
 * Downtown extract bounds [west, south, east, north].
 *
 * Deliberately tight. The full city has 343,222 building polygons, which is
 * far too much geometry to ship to a browser; this window covers downtown,
 * LoDo, Capitol Hill and the Golden Triangle — the part with the skyline that
 * makes a 3D view worth having.
 */
export const DOWNTOWN_BBOX = [-105.02, 39.72, -104.95, 39.78];

/** Denver publishes building heights in FEET. deck.gl wants metres. */
export const FEET_TO_METRES = 0.3048;

/**
 * How far above the ground a flat marking is drawn, in metres.
 *
 * Lines painted at exactly z=0 fight the basemap for the same pixels and
 * flicker as the camera moves — depth testing has no way to pick a winner
 * between two surfaces at identical depth. Half a metre is under the map's
 * own noise at any zoom you would read a road at, and it settles the contest.
 */
export const GROUND_LIFT_M = 0.5;

export function feetToMetres(feet) {
  const value = Number(feet);
  return Number.isFinite(value) ? value * FEET_TO_METRES : 0;
}

/**
 * Height for extrusion, in metres.
 *
 * Verified against reality: the tallest value in the dataset is 717 ft and
 * Republic Plaza is 714 ft, so the field is height above ground in feet, not
 * an elevation. Zero and null are common for sheds and slivers; those get a
 * small floor so they read as ground clutter rather than disappearing.
 */
export function buildingHeight(properties, { floor = 3 } = {}) {
  const metres = feetToMetres(properties?.BLDG_HEIGH);
  return metres > 0 ? metres : floor;
}

/** Inclusive point-in-bbox test. */
export function inBbox([lng, lat], [w, s, e, n]) {
  return lng >= w && lng <= e && lat >= s && lat <= n;
}

/**
 * UTC day key for a timestamp, e.g. "2026-07-26".
 *
 * Data is bucketed per day so the client fetches only the days inside the
 * scrubber window instead of the whole corpus.
 */
export function dayKey(epochMs) {
  return new Date(epochMs).toISOString().slice(0, 10);
}

/** Every day key from start to end inclusive. */
export function dayRange(startMs, endMs) {
  const days = [];
  const day = 86400000;
  let cursor = Date.UTC(
    new Date(startMs).getUTCFullYear(),
    new Date(startMs).getUTCMonth(),
    new Date(startMs).getUTCDate(),
  );
  while (cursor <= endMs) {
    days.push(dayKey(cursor));
    cursor += day;
  }
  return days;
}

/** Clamp a value into [min, max]. */
export function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Flatten GeoJSON building polygons into deck.gl rows.
 *
 * Lives here rather than beside the layer so it can be tested without pulling
 * in the whole render stack. MultiPolygon members become separate rows:
 * PolygonLayer takes one ring set per row, and splitting once at load is
 * cheaper than asking it to cope on every frame.
 */
export function toPolygons(features) {
  const rows = [];
  for (const feature of features ?? []) {
    const geometry = feature?.geometry;
    if (!geometry) continue;
    const height = Number(feature.properties?.h) || 3;
    // Denver's coarse class ("Residential", "Commercial"). Carried through so
    // the building editor can show what the survey already claims.
    const type = feature.properties?.t ?? null;

    if (geometry.type === "Polygon") {
      rows.push({ polygon: geometry.coordinates, height, type });
    } else if (geometry.type === "MultiPolygon") {
      for (const part of geometry.coordinates) {
        rows.push({ polygon: part, height, type });
      }
    }
  }
  return rows;
}


/**
 * The area Opsec has data for.
 *
 * Must stay in step with `BOUNDS` in eigensite/pulse_notes/notes.py, the bbox
 * in scripts/fetch-basemap.sh, and the map's `maxBounds`. When the basemap
 * extract was narrower than this, the ground rendered black wherever the two
 * disagreed.
 */
export const DATA_BOUNDS = {
  west: -105.25, south: 39.50, east: -104.70, north: 40.00,
};

/** Is this coordinate somewhere the map actually has anything to show? */
export function withinData(lng, lat, bounds = DATA_BOUNDS) {
  return Number.isFinite(lng) && Number.isFinite(lat)
    && lng >= bounds.west && lng <= bounds.east
    && lat >= bounds.south && lat <= bounds.north;
}
